import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../Constants';

const InvoicingEmailSetup = () => {
    const [data, setData] = useState([]);
    const [filterBy, setFilterBy] = useState('');
    const [sortBy, setSortBy] = useState(''); // Default to no sorting
    const [searchQuery, setSearchQuery] = useState('');
    const [missingEmail, setMissingEmail] = useState(false);

    // Fetch the initial data
    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch( API_BASE_URL + '/api/maintenance/invoicingemailsetup', {
                credentials: 'include',
            });
            const result = await response.json();
            setData(result);
        };
        fetchData();
    }, []);

    // Filter and sort the data based on the user's selection
    const filteredData = data
        .filter(item => {
            const matchesFilter = filterBy === '' || item.billMethod === filterBy;
            const matchesSearch = (
                (item.buildingSite && item.buildingSite.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.customer && item.customer.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.managementId && item.managementId.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.className && item.className.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.billingContact && item.billingContact.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.emailAddress && item.emailAddress.toLowerCase().includes(searchQuery.toLowerCase()))
            );
            const matchesMissingEmail = !missingEmail || !item.emailAddress;

            return matchesFilter && matchesSearch && matchesMissingEmail;
        })
        .sort((a, b) => {
            // Only sort if a valid sortBy option is selected
            if (sortBy === 'Building') {
                return a.level === 'Building' && b.level !== 'Building' ? -1 : 
                       a.level !== 'Building' && b.level === 'Building' ? 1 : 0;
            } else if (sortBy === 'Customer') {
                return a.level === 'Customer' && b.level !== 'Customer' ? -1 : 
                       a.level !== 'Customer' && b.level === 'Customer' ? 1 : 0;
            } else if (sortBy === 'Management') {
                return a.level === 'Management' && b.level !== 'Management' ? -1 : 
                       a.level !== 'Management' && b.level === 'Management' ? 1 : 0;
            } else {
                return 0; // No sorting if "Select" or invalid sortBy is chosen
            }
        });

    // Handle dropdown changes for Level and Bill Method
    const handleDropdownChange = async (id, field, value) => {
        const updatedData = data.map(item => 
            item.id === id ? { ...item, [field]: value } : item
        );
        setData(updatedData);

        // Send update to server
        await fetch( API_BASE_URL + `/api/maintenance/invoicingemailsetup/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ [field]: value }),
        });
    };

    return (
        <div className="container mx-auto">
            <h1 className="text-2xl font-bold mb-4">Contract Email Set Up</h1>
            <div className="flex mb-4">
                {/* Filter By */}
                <div className="mr-4">
                    <label className="block mb-1 font-semibold">Filter By</label>
                    <select value={filterBy} onChange={(e) => setFilterBy(e.target.value)} className="border px-4 py-2">
                        <option value="">Select</option>
                        <option value="All Methods">All Methods</option>
                        <option value="Print">Print</option>
                        <option value="Print and Email">Print and Email</option>
                        <option value="Print and Email and Fax">Print and Email and Fax</option>
                        <option value="Email">Email</option>
                        <option value="Email and Fax">Email and Fax</option>
                        <option value="Fax">Fax</option>
                    </select>
                </div>

                {/* Sort By */}
                <div className="mr-4">
                    <label className="block mb-1 font-semibold">Sort By</label>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="border px-4 py-2">
                        <option value="">Select</option> {/* Default option for no sorting */}
                        <option value="Building">Building</option>
                        <option value="Customer">Customer</option>
                        <option value="Management">Management Company</option>
                    </select>
                </div>

                {/* Search Bar */}
                <div className="mr-4">
                    <label className="block mb-1 font-semibold">Search</label>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search..."
                        className="border px-4 py-2"
                    />
                </div>
            </div>

            <table className="min-w-full border border-gray-300">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border px-4 py-2">Class</th>
                        <th className="border px-4 py-2">Management ID</th>
                        <th className="border px-4 py-2">Customer #</th>
                        <th className="border px-4 py-2">Building Site</th>
                        <th className="border px-4 py-2">Level</th>
                        <th className="border px-4 py-2">Bill Method</th>
                        <th className="border px-4 py-2">Billing Contact</th>
                        <th className="border px-4 py-2">
                            Email Address
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={missingEmail}
                                    onChange={(e) => setMissingEmail(e.target.checked)}
                                    className="ml-2"
                                />
                                <label className="ml-1">Missing</label>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {filteredData.map(item => (
                        <tr key={item.id} className="border-t">
                            <td className="border px-4 py-2">{item.className}</td>
                            <td className="border px-4 py-2">{item.managementId}</td>
                            <td className="border px-4 py-2">{item.customer}</td>
                            <td className="border px-4 py-2">{item.buildingSite}</td>
                            <td className="border px-4 py-2">
                                <select
                                    value={item.level}
                                    onChange={(e) => handleDropdownChange(item.id, 'level', e.target.value)}
                                    className="border px-2 py-1"
                                >
                                    <option value="Building">Building</option>
                                    <option value="Customer">Customer</option>
                                    <option value="Management">Management Company</option>
                                </select>
                            </td>
                            <td className="border px-4 py-2">
                                <select
                                    value={item.billMethod}
                                    onChange={(e) => handleDropdownChange(item.id, 'billMethod', e.target.value)}
                                    className="border px-2 py-1"
                                >
                                    <option value="Print">Print</option>
                                    <option value="Email">Email</option>
                                    <option value="Fax">Fax</option>
                                    <option value="Print and Email">Print and Email</option>
                                    <option value="Print and Fax">Print and Fax</option>
                                    <option value="Email and Fax">Email and Fax</option>
                                    <option value="Print and Email and Fax">Print and Email and Fax</option>
                                    <option value="All Methods">All Methods</option>
                                </select>
                            </td>
                            <td className="border px-4 py-2">{item.billingContact}</td>
                            <td className="border px-4 py-2">{item.emailAddress}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default InvoicingEmailSetup;
